import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Slider } from '@/components/ui/slider';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { FEMachine } from '@/types/flexe';
import { useFEMachines } from '@/hooks/useFEMachines';
import { toKW } from '@/lib/flexeScenarioCalculations';
import { Loader2 } from 'lucide-react';

interface MachineModalProps {
  open: boolean;
  onClose: () => void;
  machine?: FEMachine | null;
}

const WEEKDAYS = [
  { key: 'mon', label: 'Ma' },
  { key: 'tue', label: 'Di' },
  { key: 'wed', label: 'Wo' },
  { key: 'thu', label: 'Do' },
  { key: 'fri', label: 'Vr' },
  { key: 'sat', label: 'Za' },
  { key: 'sun', label: 'Zo' },
];

export function MachineModal({ open, onClose, machine }: MachineModalProps) {
  const { createMachine, updateMachine, isCreating, isUpdating } = useFEMachines();
  const isEdit = !!machine;

  // Form state
  const [name, setName] = useState('');
  const [powerMode, setPowerMode] = useState<'direct' | 'amps'>('amps');
  const [kw, setKw] = useState<number | ''>('');
  const [amps, setAmps] = useState<number | ''>('');
  const [cosphi, setCosphi] = useState(0.9);
  const [phases, setPhases] = useState(3);
  const [voltage, setVoltage] = useState(400);
  const [dutyCycle, setDutyCycle] = useState(1.0);
  const [concurrencyFactor, setConcurrencyFactor] = useState(1.0);
  const [units, setUnits] = useState(1);
  const [inrushKw, setInrushKw] = useState<number | ''>('');
  const [schedule, setSchedule] = useState<Record<string, string[][]>>({});
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [activeDay, setActiveDay] = useState('mon');

  // Initialize form when machine changes
  useEffect(() => {
    if (machine) {
      setName(machine.name);
      setPowerMode(machine.kw ? 'direct' : 'amps');
      setKw(machine.kw || '');
      setAmps(machine.amps || '');
      setCosphi(machine.cosphi);
      setPhases(machine.phases);
      setVoltage(machine.voltage);
      setDutyCycle(machine.duty_cycle);
      setConcurrencyFactor(machine.concurrency_factor);
      setUnits(machine.units);
      setInrushKw(machine.inrush_kw || '');
      setSchedule(machine.schedule || {});
      setScheduleEnabled(Object.keys(machine.schedule || {}).length > 0);
    } else {
      // Reset for new machine
      setName('');
      setPowerMode('amps');
      setKw('');
      setAmps('');
      setCosphi(0.9);
      setPhases(3);
      setVoltage(400);
      setDutyCycle(1.0);
      setConcurrencyFactor(1.0);
      setUnits(1);
      setInrushKw('');
      setSchedule({});
      setScheduleEnabled(false);
    }
  }, [machine, open]);

  const calculatedKW = powerMode === 'amps' && amps 
    ? toKW(Number(amps), cosphi, phases, voltage) 
    : powerMode === 'direct' && kw 
    ? Number(kw) 
    : 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const machineData = {
      name,
      kw: powerMode === 'direct' ? Number(kw) : undefined,
      amps: powerMode === 'amps' ? Number(amps) : undefined,
      cosphi,
      phases,
      voltage,
      duty_cycle: dutyCycle,
      concurrency_factor: concurrencyFactor,
      units,
      inrush_kw: inrushKw ? Number(inrushKw) : undefined,
      schedule: scheduleEnabled ? schedule : {},
    };

    if (isEdit && machine) {
      updateMachine({ id: machine.id, updates: machineData });
    } else {
      createMachine(machineData as any);
    }

    onClose();
  };

  const addTimeSlot = () => {
    setSchedule(prev => ({
      ...prev,
      [activeDay]: [...(prev[activeDay] || []), ['09:00', '17:00']]
    }));
  };

  const removeTimeSlot = (dayKey: string, index: number) => {
    setSchedule(prev => ({
      ...prev,
      [dayKey]: (prev[dayKey] || []).filter((_, i) => i !== index)
    }));
  };

  const updateTimeSlot = (dayKey: string, index: number, slotIndex: 0 | 1, value: string) => {
    setSchedule(prev => {
      const daySlots = [...(prev[dayKey] || [])];
      daySlots[index][slotIndex] = value;
      return { ...prev, [dayKey]: daySlots };
    });
  };

  const setQuickSchedule = (type: 'workdays' | '24/7' | 'reset') => {
    if (type === 'workdays') {
      const workdaySchedule: Record<string, string[][]> = {};
      ['mon', 'tue', 'wed', 'thu', 'fri'].forEach(day => {
        workdaySchedule[day] = [['09:00', '17:00']];
      });
      setSchedule(workdaySchedule);
    } else if (type === '24/7') {
      setSchedule({});
      setScheduleEnabled(false);
    } else {
      setSchedule({});
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Machine Bewerken' : 'Nieuwe Machine'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="basic">Basis</TabsTrigger>
              <TabsTrigger value="usage">Gebruik</TabsTrigger>
              <TabsTrigger value="schedule">Planning</TabsTrigger>
            </TabsList>

            {/* TAB 1: Basis Gegevens */}
            <TabsContent value="basic" className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Naam *</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Bijv. Laser Fiber 6 kW"
                  required
                />
              </div>

              <div className="space-y-4">
                <Label>Vermogen Specificatie</Label>
                <RadioGroup value={powerMode} onValueChange={(v: any) => setPowerMode(v)}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="direct" id="direct" />
                    <Label htmlFor="direct">Direct kW invoeren</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="amps" id="amps" />
                    <Label htmlFor="amps">Via Ampère (aanbevolen)</Label>
                  </div>
                </RadioGroup>
              </div>

              {powerMode === 'direct' ? (
                <div className="space-y-2">
                  <Label htmlFor="kw">Vermogen (kW) *</Label>
                  <Input
                    id="kw"
                    type="number"
                    value={kw}
                    onChange={(e) => setKw(e.target.value ? Number(e.target.value) : '')}
                    placeholder="60"
                    min={0}
                    step={0.1}
                    required
                  />
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="amps">Ampère *</Label>
                      <Input
                        id="amps"
                        type="number"
                        value={amps}
                        onChange={(e) => setAmps(e.target.value ? Number(e.target.value) : '')}
                        placeholder="100"
                        min={0}
                        step={0.1}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cosphi">Cosphi</Label>
                      <Input
                        id="cosphi"
                        type="number"
                        value={cosphi}
                        onChange={(e) => setCosphi(Number(e.target.value))}
                        min={0}
                        max={1}
                        step={0.01}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phases">Fasen</Label>
                      <Input
                        id="phases"
                        type="number"
                        value={phases}
                        onChange={(e) => setPhases(Number(e.target.value))}
                        min={1}
                        max={3}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="voltage">Voltage (V)</Label>
                      <Input
                        id="voltage"
                        type="number"
                        value={voltage}
                        onChange={(e) => setVoltage(Number(e.target.value))}
                        min={0}
                      />
                    </div>
                  </div>
                </>
              )}

              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm font-medium">
                  Berekend vermogen: <span className="text-primary">~{calculatedKW.toFixed(1)} kW</span>
                </p>
              </div>
            </TabsContent>

            {/* TAB 2: Gebruik & Planning */}
            <TabsContent value="usage" className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Duty Cycle: {(dutyCycle * 100).toFixed(0)}%</Label>
                </div>
                <Slider
                  value={[dutyCycle]}
                  onValueChange={(v) => setDutyCycle(v[0])}
                  min={0}
                  max={1}
                  step={0.01}
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground">
                  Gemiddelde bezetting van de machine
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Concurrency Factor: {(concurrencyFactor * 100).toFixed(0)}%</Label>
                </div>
                <Slider
                  value={[concurrencyFactor]}
                  onValueChange={(v) => setConcurrencyFactor(v[0])}
                  min={0}
                  max={1}
                  step={0.01}
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground">
                  Gelijktijdigheid met andere machines
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="units">Aantal Units</Label>
                <Input
                  id="units"
                  type="number"
                  value={units}
                  onChange={(e) => setUnits(Number(e.target.value))}
                  min={1}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="inrush_kw">Inrush Power (optioneel)</Label>
                <Input
                  id="inrush_kw"
                  type="number"
                  value={inrushKw}
                  onChange={(e) => setInrushKw(e.target.value ? Number(e.target.value) : '')}
                  placeholder="30"
                  min={0}
                />
                <p className="text-xs text-muted-foreground">
                  Pikvermogen bij opstarten (kW)
                </p>
              </div>
            </TabsContent>

            {/* TAB 3: Planning */}
            <TabsContent value="schedule" className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Schema inschakelen</Label>
                <Switch
                  checked={scheduleEnabled}
                  onCheckedChange={setScheduleEnabled}
                />
              </div>

              {scheduleEnabled && (
                <>
                  <div className="space-y-2">
                    <Label>Werkdagen</Label>
                    <div className="flex gap-2">
                      {WEEKDAYS.map(day => (
                        <Button
                          key={day.key}
                          type="button"
                          variant={schedule[day.key]?.length ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setActiveDay(day.key)}
                          className={activeDay === day.key ? 'ring-2 ring-primary' : ''}
                        >
                          {day.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Tijd Vensters ({WEEKDAYS.find(d => d.key === activeDay)?.label})</Label>
                    {(schedule[activeDay] || []).map((slot, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Input
                          type="time"
                          value={slot[0]}
                          onChange={(e) => updateTimeSlot(activeDay, index, 0, e.target.value)}
                        />
                        <span className="text-sm text-muted-foreground">tot</span>
                        <Input
                          type="time"
                          value={slot[1]}
                          onChange={(e) => updateTimeSlot(activeDay, index, 1, e.target.value)}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeTimeSlot(activeDay, index)}
                        >
                          ✕
                        </Button>
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={addTimeSlot}>
                      + Venster toevoegen
                    </Button>
                  </div>

                  <div className="flex gap-2">
                    <Button type="button" variant="secondary" size="sm" onClick={() => setQuickSchedule('workdays')}>
                      Werkdagen 09-17
                    </Button>
                    <Button type="button" variant="secondary" size="sm" onClick={() => setQuickSchedule('24/7')}>
                      24/7
                    </Button>
                    <Button type="button" variant="secondary" size="sm" onClick={() => setQuickSchedule('reset')}>
                      Reset
                    </Button>
                  </div>
                </>
              )}
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={onClose}>
              Annuleren
            </Button>
            <Button type="submit" disabled={isCreating || isUpdating}>
              {(isCreating || isUpdating) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEdit ? 'Bijwerken' : 'Toevoegen'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
